import { createContext, useContext, useEffect, useState } from "react";
import { playFlyToCartAnimation } from "../lib/cart-add-animation";

export type CartItemModifier = {
  modifierId: string;
  modifierName: string;
  optionValue: string;
  optionLabel: string;
  priceDelta: number;
};

export type CartItem = {
  id: string; // Unique ID for this cart item
  productId: string;
  productName: string;
  variantId: string;
  variantName: string;
  price: number;
  quantity: number;
  modifiers: CartItemModifier[];
  selectedOptions: Record<string, string>; // optionId -> optionValueId
  note?: string; // Optional note for this item
  totalPrice: number; // (price + modifier adjustments) * quantity
};

export type CartAddFeedback = {
  token: number;
  itemId: string;
  productName: string;
  quantity: number;
};

export type AddItemOptions = {
  animationSource?: HTMLElement | null;
};

type CartContextType = {
  items: CartItem[];
  addItem: (
    item: Omit<CartItem, "id" | "totalPrice">,
    options?: AddItemOptions,
  ) => string;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateNote: (itemId: string, note: string) => void;
  splitItem: (itemId: string, splitQuantity: number) => void;
  mergeItems: (itemId1: string, itemId2: string) => void;
  clearCart: () => void;
  total: number;
  itemCount: number;
  lastAddedFeedback: CartAddFeedback | null;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

const STORAGE_KEY = "posCart";

// Helper function to notify other tabs/windows about cart changes
const notifyCartUpdate = () => {
  // Dispatch custom event for same-tab sync
  window.dispatchEvent(new CustomEvent("cart-updated"));
};

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [lastAddedFeedback, setLastAddedFeedback] =
    useState<CartAddFeedback | null>(null);

  useEffect(() => {
    // Load from localStorage on mount
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CartItem[];
        setItems(parsed);
      } catch {
        // Invalid data, clear it
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  const calculateTotalPrice = (
    price: number,
    modifiers: CartItemModifier[],
    quantity: number,
  ): number => {
    const modifierTotal = modifiers.reduce(
      (sum, mod) => sum + mod.priceDelta,
      0,
    );
    return (price + modifierTotal) * quantity;
  };

  const addItem = (
    item: Omit<CartItem, "id" | "totalPrice">,
    options?: AddItemOptions,
  ) => {
    const id = `${item.variantId}-${Date.now()}-${Math.random()}`;
    const totalPrice = calculateTotalPrice(
      item.price,
      item.modifiers,
      item.quantity,
    );

    const newItem: CartItem = {
      ...item,
      id,
      totalPrice,
    };

    setItems((prev) => {
      const updated = [newItem, ...prev];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      notifyCartUpdate();
      return updated;
    });

    setLastAddedFeedback({
      token: Date.now(),
      itemId: id,
      productName: item.productName,
      quantity: item.quantity,
    });

    playFlyToCartAnimation(options?.animationSource, item.quantity);

    return id;
  };

  const removeItem = (itemId: string) => {
    setItems((prev) => {
      const updated = prev.filter((item) => item.id !== itemId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      notifyCartUpdate();
      return updated;
    });
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }

    setItems((prev) => {
      const updated = prev.map((item) => {
        if (item.id === itemId) {
          const totalPrice = calculateTotalPrice(
            item.price,
            item.modifiers,
            quantity,
          );
          return { ...item, quantity, totalPrice };
        }
        return item;
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      notifyCartUpdate();
      return updated;
    });
  };

  const updateNote = (itemId: string, note: string) => {
    setItems((prev) => {
      const updated = prev.map((item) => {
        if (item.id === itemId) {
          return { ...item, note: note.trim() || undefined };
        }
        return item;
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      notifyCartUpdate();
      return updated;
    });
  };

  const splitItem = (itemId: string, splitQuantity: number) => {
    setItems((prev) => {
      const item = prev.find((i) => i.id === itemId);
      if (!item || splitQuantity <= 0 || splitQuantity >= item.quantity) {
        return prev;
      }

      const remainingQuantity = item.quantity - splitQuantity;
      const splitItemId = `${item.variantId}-${Date.now()}-${Math.random()}`;

      const updated = prev.map((i) => {
        if (i.id === itemId) {
          const totalPrice = calculateTotalPrice(
            i.price,
            i.modifiers,
            remainingQuantity,
          );
          return { ...i, quantity: remainingQuantity, totalPrice };
        }
        return i;
      });

      const newItem: CartItem = {
        ...item,
        id: splitItemId,
        quantity: splitQuantity,
        totalPrice: calculateTotalPrice(
          item.price,
          item.modifiers,
          splitQuantity,
        ),
      };

      updated.push(newItem);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      notifyCartUpdate();
      return updated;
    });
  };

  const mergeItems = (itemId1: string, itemId2: string) => {
    setItems((prev) => {
      const item1 = prev.find((i) => i.id === itemId1);
      const item2 = prev.find((i) => i.id === itemId2);

      if (!item1 || !item2) {
        return prev;
      }

      // Check if items can be merged (same variant, modifiers, and options)
      if (
        item1.variantId !== item2.variantId ||
        JSON.stringify(item1.selectedOptions) !==
          JSON.stringify(item2.selectedOptions)
      ) {
        return prev;
      }

      // Check modifiers match
      if (item1.modifiers.length !== item2.modifiers.length) {
        return prev;
      }

      const modifiersMatch = item1.modifiers.every((mod1) => {
        return item2.modifiers.some(
          (mod2) =>
            mod1.modifierId === mod2.modifierId &&
            mod1.optionValue === mod2.optionValue,
        );
      });

      if (!modifiersMatch) {
        return prev;
      }

      // Merge items
      const mergedQuantity = item1.quantity + item2.quantity;
      const mergedNote =
        item1.note && item2.note && item1.note !== item2.note
          ? `${item1.note}; ${item2.note}`
          : item1.note || item2.note;

      const totalPrice = calculateTotalPrice(
        item1.price,
        item1.modifiers,
        mergedQuantity,
      );

      // Create merged item based on item1
      const mergedItem: CartItem = {
        ...item1,
        quantity: mergedQuantity,
        note: mergedNote,
        totalPrice,
      };

      // Remove both items and add merged item
      const updated = prev
        .filter((i) => i.id !== itemId1 && i.id !== itemId2)
        .concat(mergedItem);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      notifyCartUpdate();
      return updated;
    });
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(STORAGE_KEY);
    notifyCartUpdate();
  };

  const total = items.reduce((sum, item) => sum + item.totalPrice, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        updateNote,
        splitItem,
        mergeItems,
        clearCart,
        total,
        itemCount,
        lastAddedFeedback,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
